import {
    DOMAIN_CONFIGS,
    DomainFormHook,
    DomainHookProps,
    useAppEnum,
    useFormLifeCycle,
} from "@/Shared";

import { PermissionFormViewModel } from '../ViewModels';
import { PermissionValidationSchema } from "../PermissionFormValidation";
import { PermissionModel } from "../Models";


/**
 * usePermissionForm hook – manages permission form data and interactions.
 * 
 * Author: Samano CASTRE  
 * Date: July 17, 2025
 * 
 * Responsibilities:
 * - Initialises permission form data by fetching current permission and agent list
 * - Tracks form submission state and response (create or update)
 * - Exposes form logic to the component layer
 * 
 * Behavior:
 * - On mount, fetches the current permission (if editing) and the list of agents
 * - Automatically switches between `createPermission` or `updatePermission` based on permission presence
 * - Tracks the response of the latest action to provide submission feedback
 */
export const usePermissionForm = (props: DomainHookProps): DomainFormHook => {
    const { permissionHook } = props;
    const { domainType } = DOMAIN_CONFIGS.Permission;

    /**
     * Permission-related enums exposed to the UI layer
     */
    const { PermissionEnums } = useAppEnum();

    const lifeCycleHook = useFormLifeCycle({
        response: permissionHook.stateResponse,
        domainType,
        props,
        entityHook: permissionHook,
        buildVM: (currentEntity: PermissionModel) => {
            return new PermissionFormViewModel(
                PermissionEnums,
                currentEntity
            )
        },
    });

    /**
     * Public API exposed to the component layer.
     * Only data and actions strictly required by the UI are returned.
     */
    return {
        // Form
        ...lifeCycleHook,
        viewEnums: PermissionEnums,
        ValidationSchema: PermissionValidationSchema(PermissionEnums),
    }
}